import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { logout, updateMe } from '../lib/api';
import { useSession } from '../lib/session';
import { useRouter } from '../lib/router';
import { LOCALE_LABELS, SUPPORTED_LOCALES, useT, type SupportedLocale } from '../lib/i18n';
import { MainButton } from '../lib/MainButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { TextInput, TextArea } from '../components/TextInput';
import { Badge } from '../components/Badge';
import { InviteCard } from '../components/InviteCard';
import { RouteAlertsCard } from '../components/RouteAlertsCard';
import { ProductsCarousel } from '../components/ProductsCarousel';
import { VibesCard } from '../components/VibesCard';
import { useToast } from '../components/Toast';
import { deriveDriverTier, nextTierTarget, type Vibes } from '@waylo/shared';
import { hapticSuccess } from '../lib/telegram';

export function ProfilePage(): JSX.Element {
  const { me, refresh } = useSession();
  const { navigate } = useRouter();
  const { t, locale, setLocale } = useT();
  const toast = useToast();
  const [name, setName] = useState(me?.displayName ?? '');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [isDriver, setIsDriver] = useState(me?.isDriver ?? false);
  const [carMake, setCarMake] = useState(me?.carMake ?? '');
  const [carModel, setCarModel] = useState(me?.carModel ?? '');
  const [carColor, setCarColor] = useState(me?.carColor ?? '');
  const [carPlate, setCarPlate] = useState(me?.carPlate ?? '');
  const [carAc, setCarAc] = useState(me?.carAc ?? false);
  const [carCharger, setCarCharger] = useState(me?.carCharger ?? false);
  const [carMiddleSeat, setCarMiddleSeat] = useState(me?.carMiddleSeat ?? false);
  const [phone, setPhone] = useState(me?.phone ?? '');
  const [phoneVisible, setPhoneVisible] = useState(me?.phoneVisible ?? false);
  const [telegramVisible, setTelegramVisible] = useState(me?.telegramVisible ?? true);
  const [bioVisible, setBioVisible] = useState(me?.bioVisible ?? true);
  const [emergencyName, setEmergencyName] = useState(me?.emergencyContactName ?? '');
  const [emergencyPhone, setEmergencyPhone] = useState(me?.emergencyContactPhone ?? '');
  const [vibes, setVibes] = useState<Vibes>(
    me?.vibes ?? {
      music: null,
      smoking: null,
      pets: null,
      luggage: null,
      womenOnly: null,
      chatLevel: null,
    },
  );
  const [saved, setSaved] = useState(false);

  const phoneTrim = phone.replace(/\s+/g, '');
  const phoneValid = phoneTrim === '' || /^\+\d{6,15}$/.test(phoneTrim);
  const emergencyPhoneTrim = emergencyPhone.replace(/\s+/g, '');
  const emergencyPhoneValid =
    emergencyPhoneTrim === '' || /^\+\d{6,15}$/.test(emergencyPhoneTrim);

  const save = useMutation({
    mutationFn: () =>
      updateMe({
        displayName: name.trim(),
        bio: bio.trim(),
        isDriver,
        carMake: carMake.trim() || undefined,
        carModel: carModel.trim() || undefined,
        carColor: carColor.trim() || undefined,
        carPlate: carPlate.trim() || undefined,
        carAc,
        carCharger,
        carMiddleSeat,
        locale,
        phone: phoneTrim,
        phoneVisible: !!phoneTrim && phoneVisible,
        telegramVisible,
        bioVisible,
        emergencyContactName: emergencyName.trim() || '',
        emergencyContactPhone: emergencyPhoneTrim,
        vibes,
      }),
    onSuccess: async () => {
      hapticSuccess();
      setSaved(true);
      toast(t('common.saved'));
      await refresh();
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const doLogout = useMutation({
    mutationFn: logout,
    onSuccess: () => navigate('/'),
  });

  const handleLocaleChange = (next: SupportedLocale): void => {
    void setLocale(next, false); // persist on save() to bundle with other profile fields
  };

  if (!me) return <div className="p-6">{t('common.loading')}</div>;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <header className="mb-4 flex items-center gap-3">
        {me.avatarUrl ? (
          <img src={me.avatarUrl} alt="" className="h-14 w-14 rounded-full" />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-navy text-brand-cream">
            {me.displayName.slice(0, 1)}
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-xl font-bold text-tg-text">{me.displayName}</h1>
          <p className="text-xs text-tg-hint">
            {t('profile.ridesCompleted', { n: me.ridesCompleted })}
            {me.ratingAvg !== null && ` · ★ ${me.ratingAvg.toFixed(1)}`}
          </p>
        </div>
        {me.isPremium && <Badge tone="brand">{t('premium.tag')}</Badge>}
      </header>

      {me.badges.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {me.badges.map((b) => (
            <Badge key={b} tone="success">
              {t(`badge.${b}` as 'badge.founding_driver')}
            </Badge>
          ))}
        </div>
      )}

      {me.isDriver &&
        (() => {
          const tierInput = {
            isDriver: me.isDriver,
            ridesCompleted: me.ridesCompleted,
            ratingAvg: me.ratingAvg,
          };
          const tier = deriveDriverTier(tierInput);
          const next = nextTierTarget(tierInput);
          const pct = next
            ? Math.min(100, Math.round((me.ridesCompleted / next.ridesNeeded) * 100))
            : 100;
          const toGo = next ? Math.max(0, next.ridesNeeded - me.ridesCompleted) : 0;
          return (
            <Card className="mb-3 space-y-2">
              <div className="flex items-center justify-between">
                <CardTitle>{t('profile.tier.title')}</CardTitle>
                <Badge tone="brand">{t(tier.labelKey as 'tier.newcomer')}</Badge>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-tg-bg-secondary">
                <div
                  className="h-full rounded-full bg-brand-green transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <CardHint>
                {next
                  ? t('profile.tier.toGo', {
                      n: toGo,
                      next: t(`tier.${next.nextTier}` as 'tier.regular'),
                    })
                  : t('profile.tier.max')}
              </CardHint>
              <p className="pt-1 text-sm font-medium text-tg-text">
                {t('reliability.yours', { n: me.reliability })}
              </p>
            </Card>
          );
        })()}

      <div className="space-y-3">
        <Card className="space-y-3">
          <CardTitle>{t('profile.section.profile')}</CardTitle>
          <TextInput
            id="name"
            label={t('profile.displayName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
          />
          <TextArea
            id="bio"
            label={t('profile.bio')}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={280}
          />
          <label className="flex items-center justify-between">
            <span className="text-tg-text">{t('profile.driverMode')}</span>
            <input
              type="checkbox"
              checked={isDriver}
              onChange={(e) => setIsDriver(e.target.checked)}
              className="h-5 w-5 accent-brand-green"
            />
          </label>
        </Card>

        <Card className="space-y-3">
          <CardTitle>{t('phone.label')}</CardTitle>
          <TextInput
            id="phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('phone.placeholder')}
            error={!phoneValid ? t('phone.invalid') : undefined}
          />
          <CardHint>{t('phone.hint')}</CardHint>
        </Card>

        <Card className="space-y-3">
          <CardTitle>{t('privacy.title')}</CardTitle>
          <CardHint>{t('privacy.contactReveal')}</CardHint>
          <PrivacyToggle
            label={t('privacy.shareTelegram')}
            value={telegramVisible}
            onChange={setTelegramVisible}
          />
          <PrivacyToggle
            label={t('privacy.sharePhone')}
            value={phoneVisible}
            disabled={!phoneTrim}
            onChange={setPhoneVisible}
          />
          <PrivacyToggle
            label={t('privacy.showBio')}
            value={bioVisible}
            onChange={setBioVisible}
          />
        </Card>

        <Card className="space-y-3">
          <CardTitle>{t('profile.emergency.title')}</CardTitle>
          <CardHint>{t('profile.emergency.hint')}</CardHint>
          <TextInput
            id="emergency-name"
            label={t('profile.emergency.name')}
            value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)}
            maxLength={60}
          />
          <TextInput
            id="emergency-phone"
            label={t('profile.emergency.phone')}
            type="tel"
            inputMode="tel"
            value={emergencyPhone}
            onChange={(e) => setEmergencyPhone(e.target.value)}
            placeholder={t('phone.placeholder')}
            error={!emergencyPhoneValid ? t('phone.invalid') : undefined}
          />
        </Card>

        <Card className="space-y-3">
          <CardTitle>{t('profile.language')}</CardTitle>
          <div className="grid grid-cols-2 gap-2">
            {SUPPORTED_LOCALES.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => handleLocaleChange(l)}
                className={
                  'rounded-input border px-3 py-2 text-sm font-medium transition ' +
                  (locale === l
                    ? 'border-transparent bg-[var(--border-medium)] font-semibold text-tg-text'
                    : 'border-[var(--border-light)] text-tg-hint')
                }
              >
                {LOCALE_LABELS[l]}
              </button>
            ))}
          </div>
        </Card>

        {isDriver && (
          <Card className="space-y-3">
            <CardTitle>{t('profile.section.car')}</CardTitle>
            <CardHint>{t('profile.section.car.hint')}</CardHint>
            <TextInput
              id="car-make"
              label={t('profile.car.make')}
              value={carMake}
              onChange={(e) => setCarMake(e.target.value)}
            />
            <TextInput
              id="car-model"
              label={t('profile.car.model')}
              value={carModel}
              onChange={(e) => setCarModel(e.target.value)}
            />
            <TextInput
              id="car-color"
              label={t('profile.car.color')}
              value={carColor}
              onChange={(e) => setCarColor(e.target.value)}
            />
            <TextInput
              id="car-plate"
              label={t('profile.car.plate')}
              value={carPlate}
              onChange={(e) => setCarPlate(e.target.value)}
            />
            <ComfortToggle label={t('comfort.ac')} checked={carAc} onChange={setCarAc} />
            <ComfortToggle
              label={t('comfort.charger')}
              checked={carCharger}
              onChange={setCarCharger}
            />
            <ComfortToggle
              label={t('comfort.middleSeat')}
              checked={carMiddleSeat}
              onChange={setCarMiddleSeat}
            />
          </Card>
        )}

        <VibesCard value={vibes} onChange={setVibes} />

        <RouteAlertsCard nameKey={locale === 'en' ? 'nameEn' : 'nameSr'} />

        <InviteCard />

        <ProductsCarousel />

        <Card className="space-y-2">
          <CardTitle>{t('profile.section.account')}</CardTitle>
          <button
            type="button"
            onClick={() => navigate('/leaderboard')}
            className="flex w-full items-center justify-between rounded-input bg-tg-bg-secondary px-3 py-2.5 text-sm font-medium text-tg-text"
          >
            <span>🏆 {t('leaderboard.title')}</span>
            <span className="text-tg-hint" aria-hidden>
              ›
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/premium')}
            className="flex w-full items-center justify-between rounded-input bg-tg-bg-secondary px-3 py-2.5 text-sm font-medium text-tg-text"
          >
            <span>⭐ {t('premium.tag')}</span>
            <span className="text-tg-hint" aria-hidden>
              ›
            </span>
          </button>
          <button
            type="button"
            onClick={() => doLogout.mutate()}
            className="rounded-input bg-tg-bg-secondary px-3 py-2 text-sm font-medium text-destructive"
          >
            {t('profile.logout')}
          </button>
          <div className="flex flex-wrap gap-3 pt-2 text-xs">
            <button
              type="button"
              onClick={() => navigate('/privacy')}
              className="text-tg-link underline"
            >
              {t('legal.privacy')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/terms')}
              className="text-tg-link underline"
            >
              {t('legal.terms')}
            </button>
            <a
              href="https://triardor.com/en/"
              target="_blank"
              rel="noreferrer"
              className="text-tg-link underline"
            >
              {t('common.madeByTriardor')}
            </a>
          </div>
        </Card>
      </div>

      <MainButton
        text={saved ? `✓ ${t('common.saved')}` : t('profile.save')}
        loading={save.isPending}
        disabled={!name.trim() || !phoneValid || !emergencyPhoneValid}
        onClick={() => save.mutate()}
      />
    </div>
  );
}

function PrivacyToggle({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label
      className={
        'flex items-center justify-between gap-3 rounded-input bg-tg-bg p-3 ' +
        (disabled ? 'opacity-50' : '')
      }
    >
      <span className="text-sm text-tg-text">{label}</span>
      <input
        type="checkbox"
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 accent-brand-green"
      />
    </label>
  );
}

function ComfortToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm text-tg-text">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-6 w-6 flex-none accent-brand-green"
      />
    </label>
  );
}
